import { SelectAll_s } from "components/folder/reports/report-styles"
import FormAutomation from 'components/form-automation'
import { BiDataExport_s, ExportType_s, PropertyTable_s } from 'components/import-export/import-export-styles'
// Components
import ListboxInput from "components/inputs/listbox"
import DropdownInput from "components/inputs/dropdown"
import DataTable_s from 'components/table/data-table-styles'
import exportService from "data/export"
// Data
import folderService from "data/folder"
import { parseDate } from 'data/lib/dates'
import { local } from 'data/lib/local-json'
import notificationService from "data/notification"
import propertyService from "data/property"
import socketService, * as eventType from "data/socket"
import underwritingService from 'data/underwriting'
import _ from 'lodash'
import Moment from 'moment'
import { Checkbox } from 'primereact/checkbox'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import React, { useEffect, useState } from "react"
// Styles
import { ButtonWithPress_s, Label_s } from 'styles/app-styles'
import { InputText_s } from 'styles/input-styles'
import { DateText_s } from 'styles/report-styles'

const BiDataExport = () => {
  const isFolder = window.location.pathname.includes("/folder");
  const [startDate, setStartDate] = useState<any>(underwritingService.getReportBeginDefault(!isFolder))
  const currentFolder = folderService.get()
  const [currentProperty, setProperty] = useState<object | null>(propertyService.get())
  const [propCalcd, setPropCalcd] = useState<boolean>(currentProperty?.['calcdetail'] ? currentProperty?.['calcdetail']['status'].startsWith('Calculated') : false)
  const initTerm = isFolder ? currentFolder['term'] : currentProperty?.['term']
  const defaultTerm = initTerm <= 10 ? initTerm : 10
  const [term, setTerm] = useState<number>(defaultTerm)
  const [description, setDescription] = useState<string>(getPlaceholderDescription)
  const [exportFileType, setExportFileType] = useState<string>("JSON")
  const [properties, setProperties] = useState<object[]>([])
  const [data, setData] = useState<object[]>([])
  const [selected, updateSelected] = useState<object[]>([])
  const [exportTypes, setExportTypes] = useState<object[]>(getExportTypes())
  const [exportType, setExportType] = useState<string>(exportTypes[0]['value'])

  function getPlaceholderDescription(): string {
    const created = Moment().utc().local().format('MM/DD/YYYY h:mm:ss A');
    const zoneName = Moment.tz.guess();
    const timezone = Moment.tz(zoneName).zoneAbbr();
    return `${isFolder ? 'Folder' : 'Property'} BI Export on: ${created} ${timezone}`;
  }

  function getAnalysisBeginDate() {
    return isFolder ? currentFolder['reportbegindate'] : currentProperty?.['analysisbegindate']
  }

  function getReportBeginDate() {
    return isFolder ? currentFolder['reportbegindate'] : currentProperty?.['reportbegindate']
  }

  function getReportTermMax(beginDate): number {
    const analysisBegin = parseDate(getAnalysisBeginDate());
    const reportBegin = parseDate(getReportBeginDate());
    let reportStart = beginDate ? parseDate(beginDate) : reportBegin;
    let maxTerm = Math.floor((12 * (analysisBegin.year() - reportStart.year() + initTerm)
      + (analysisBegin.month() + 1 - (reportStart.month() + 1))) / 12)

    if (maxTerm > 35) maxTerm = 35;
    if (maxTerm < 1) maxTerm = 1;

    return maxTerm;
  }

  function validateTerm(startDate, reportTerm): void {
    startDate = parseDate(startDate).format('MM/DD/YYYY');
    let maxTerm = isFolder ? 35 : getReportTermMax(startDate);

    if (reportTerm > maxTerm) {
      setTerm(maxTerm)
      notificationService.notify({
        type: "warn",
        message: "Term adjusted to term max",
        dismiss: 3000
      })
    } else if (reportTerm < 1) {
      setTerm(1)
      notificationService.notify({
        type: "warn",
        message: "Term adjusted to 1",
        dismiss: 3000
      })
    } else {
      setTerm(reportTerm)
    }
  }

  function validateStartDate(val) {
    const minStartDate = parseDate(getAnalysisBeginDate());
    const maxStartDate = `${minStartDate.month() === 0 ? 1 : minStartDate.month()}/${minStartDate.dates()}/${minStartDate.year() + getReportTermMax(minStartDate)}`

    if (parseDate(val).isBefore(parseDate(minStartDate))) {
      setStartDate(minStartDate)
      notificationService.notify({
        type: "warn",
        message: "Start date adjusted to Analysis begin date",
        dismiss: 3000
      })
    } else if (parseDate(val).isAfter(parseDate(maxStartDate)) && isFolder) {
      setStartDate(maxStartDate)
      notificationService.notify({
        type: "warn",
        message: "Start date adjusted to max",
        dismiss: 3000
      })
      validateTerm(maxStartDate, term);
    } else {
      setStartDate(val)
    }
  }

  function getExportTypes(): object[] {
    let exportTypes;
    if (isFolder) {
      exportTypes = [
        { value: 'inflation_increase_input', label: 'Input: Inflation/Increase' },
        { value: 'line_item_input', label: 'Input: Line Item' },
        { value: 'market_lease_input', label: 'Input: Market Lease' },
        { value: 'named_item_input', label: 'Input: Named Item' },
        { value: 'property_input', label: 'Input: Property' },
        { value: 'recovery_input', label: 'Input: Recovery' },
        { value: 'rent_roll_input', label: 'Input: Rent Roll' },
        { value: 'room_input', label: 'Input: Room' },
        { value: 'marketlease_ts', label: 'Results: Market Lease' },
        { value: 'named_item_ts', label: 'Results: Named Item' },
        { value: 'property_ts', label: 'Results: Property' },
        { value: 'room_ts', label: 'Results: Room' },
        { value: 'tenant_ts', label: 'Results: Tenant' }
      ]
    } else if (currentProperty?.['type'] === 'Commercial') {
      exportTypes = [
        { value: 'inflation_increase_input', label: 'Input: Inflation/Increase' },
        { value: 'line_item_input', label: 'Input: Line Item' },
        { value: 'market_lease_input', label: 'Input: Market Lease' },
        { value: 'named_item_input', label: 'Input: Named Item' },
        { value: 'property_input', label: 'Input: Property' },
        { value: 'recovery_input', label: 'Input: Recovery' },
        { value: 'rent_roll_input', label: 'Input: Rent Roll' },
        { value: 'marketlease_ts', label: 'Results: Market Lease' },
        { value: 'named_item_ts', label: 'Results: Named Item' },
        { value: 'property_ts', label: 'Results: Property' },
        { value: 'tenant_ts', label: 'Results: Tenant' }
      ]
    } else if (currentProperty?.['type'] === 'Mixed-Use') {
      exportTypes = [
        { value: 'inflation_increase_input', label: 'Input: Inflation/Increase' },
        { value: 'line_item_input', label: 'Input: Line Item' },
        { value: 'market_lease_input', label: 'Input: Market Lease' },
        { value: 'multifamily_rent_roll_input', label: 'Input: Multifamily Rent Roll' },
        { value: 'named_item_input', label: 'Input: Named Item' },
        { value: 'property_input', label: 'Input: Property' },
        { value: 'recovery_input', label: 'Input: Recovery' },
        { value: 'rent_roll_input', label: 'Input: Rent Roll' },
        { value: 'room_input', label: 'Input: Room' },
        { value: 'marketlease_ts', label: 'Results: Market Lease' },
        { value: 'multifamily_units_ts', label: 'Results: Multifamily Units' },
        { value: 'named_item_ts', label: 'Results: Named Item' },
        { value: 'property_ts', label: 'Results: Property' },
        { value: 'room_ts', label: 'Results: Room' },
        { value: 'tenant_ts', label: 'Results: Tenant' }
      ]
    } else if (currentProperty?.['type'] === 'Multifamily') {
      exportTypes = [
        { value: 'inflation_increase_input', label: 'Input: Inflation/Increase' },
        { value: 'line_item_input', label: 'Input: Line Item' },
        { value: 'property_input', label: 'Input: Property' },
        { value: 'multifamily_rent_roll_input', label: 'Input: Multifamily Rent Roll' },
        { value: 'property_ts', label: 'Results: Property' },
        { value: 'multifamily_units_ts', label: 'Results: Multifamily Units' }
      ]
    } else {
      exportTypes = [
        { value: 'inflation_increase_input', label: 'Input: Inflation/Increase' },
        { value: 'line_item_input', label: 'Input: Line Item' },
        { value: 'property_input', label: 'Input: Property' },
        { value: 'room_input', label: 'Input: Room' },
        { value: 'property_ts', label: 'Results: Property' },
        { value: 'room_ts', label: 'Results: Rooms' }
      ]
    }
    return exportTypes
  }

  let disabledExportTypes = [
    { value: 'marketlease_ts', label: 'Results: Market Lease' },
    { value: 'multifamily_units_ts', label: 'Results: Multifamily Units' },
    { value: 'named_item_ts', label: 'Results: Named Item' },
    { value: 'property_ts', label: 'Results: Property' },
    { value: 'tenant_ts', label: 'Results: Tenant' },
    { value: 'room_ts', label: 'Results: Rooms' }
  ]


  const submit = () => {
    let payload = {
      beginDate: underwritingService.toISOdate(startDate),
      context: isFolder ? 'folder' : 'property',
      description,
      report: exportType,
      reportTerm: term,
      ext: exportFileType,
      output: {
        timezoneOffset: (new Date()).getTimezoneOffset()
      }
    };

    if (isFolder) {
      payload['folderid'] = currentFolder['folderid']
      payload['propertyid'] = selected.map(property => property['propertyid']);
    } else {
      payload['propertyid'] = [currentProperty?.['propertyid']]
    }
    if (isFolder && exportType.endsWith('ts')) {
      for (let i of selected) {
        if (!i['calcdetail']['status'].startsWith('Calculated')) {
          notificationService.notify({
            type: "warn",
            message: "All selected properties must be calculated to run this export",
            dismiss: 5000
          })
          return
        }
      }
    }

    exportService.biDataExport(payload)
  }

  useEffect(() => {

    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.value.length > 0) {
        setProperties(data.value)
        setData(data.value)
        const property = _.find(data.value, { propertyid: data.current });
        setProperty(property)
      }
    })

    const socketSubscription = socketService.stream().subscribe(socket => {
      switch (true) {
        case socket.eventType === eventType.DATA_EXPORT_READY:
          notificationService.notify({
            type: "info",
            message: "BI Data Export is ready. Please check the Dashboard.",
            dismiss: 5000
          })
          break
      }
    })

    propertyService.fetchByFolderId(_.last(local('folderPath')), false)

    return function cleanup() {
      propertySubscription.unsubscribe()
      socketSubscription.unsubscribe()
    }

  }, []);

  useEffect(() => {
    if (!isFolder && (!propCalcd || !startDate || !term)) {
      setExportTypes(_.differenceWith(exportTypes, disabledExportTypes, _.isEqual))
    }
  }, [propCalcd, startDate, term]);

  useEffect(() => {
    if (!isFolder && (!propCalcd || !startDate || !term)) {
      setExportTypes(_.differenceWith(getExportTypes(), disabledExportTypes, _.isEqual))
    } else {
      setExportTypes(getExportTypes())
    }
    setPropCalcd(currentProperty?.['calcdetail'] ? currentProperty?.['calcdetail']['status'].startsWith('Calculated') : false)
    const initTerm = isFolder ? currentFolder['term'] : currentProperty?.['term']
    const defaultTerm = initTerm <= 10 ? initTerm : 10
    setTerm(defaultTerm)
  }, [currentProperty]);

  useEffect(() => {
    if (exportType) {
      let hideMFProperties = [
        'market_lease_input', 'named_item_input', 'recovery_input', 'rent_roll_input',
        'marketlease_ts', 'named_item_ts', 'tenant_ts'
      ]

      const mfProperties = properties.filter(p => p['type'] === 'Multifamily')
      const commercialProperties = properties.filter(p => p['type'] === 'Commercial');
      const hospitalityProperties = properties.filter(p => p['type'] === 'Hospitality')
      let selectedProperties: object[] = [];

      if (exportType === 'multifamily_rent_roll_input' || exportType == 'multifamily_units_ts') {
        setData(mfProperties)
        selectedProperties = mfProperties;
      } else if (exportType === 'room_input' || exportType === 'room_ts') {
        setData(hospitalityProperties)
        selectedProperties = hospitalityProperties;
      } else if (hideMFProperties.includes(exportType)) {
        setData(commercialProperties)
        selectedProperties = commercialProperties;
      } else {
        setData(properties)
        selectedProperties = properties
      }

      if (exportType.endsWith('ts')) {
        selectedProperties = selectedProperties.filter(p => p['calcdetail']?.['status'].startsWith('Calculated'));
      }

      updateSelected(selectedProperties);
    }

  }, [exportType, properties]);

  useEffect(() => {
    if (_.find(selected, p => !p['calcdetail']?.['status'].startsWith('Calculated')) && !exportType.endsWith('input')) {
      notificationService.notify({
        type: "warn",
        message: "Properties must be calculated to be included",
        dismiss: 5000
      })
    }
  }, [selected]);

  const disabledExportBtn = isFolder && (selected.length === 0 || (_.find(selected, p => p.type === 'Multifamily') && (!exportType.endsWith('input') && exportType !== 'property_ts' && exportType !== 'multifamily_units_ts')) || (exportType.endsWith('ts') && _.find(selected, p => !p.calcdetail?.status.startsWith('Calculated'))))

  return (
    <BiDataExport_s id="biDataExport">
      <div>
        <div>
          <div className="bi-data-export-input">
            <label>Start Date</label>
            <DateText_s
              className="automated"
              value={parseDate(startDate).format('MM/DD/YYYY')}
              onChange={validateStartDate}
              onBlur={validateStartDate}
              persist={validateStartDate}
              withoutDays
            />
          </div>
          <div className="bi-data-export-input">
            <label>Term</label>
            <InputText_s
              className="automated"
              keyfilter="pnum"
              onChange={(e) => validateTerm(startDate, e.target.value)}
              value={term}
            />
          </div>
          <div className="bi-data-export-input">
            <label>Description</label>
            <InputText_s
              className="automated"
              onChange={(e) => setDescription(e.target.value)}
              value={description}
            />
          </div>
          <div className="bi-data-export-input">
            <label htmlFor="exportFileType">Export File Type</label>
            <DropdownInput
              className="automated"
              options={['JSON', 'CSV'].map(opt => { return { label: opt, value: opt.toLowerCase() } })}
              value={exportFileType}
              persist={(field: string, val: string) => {
                if (val) setExportFileType(val)
              }} />
          </div>
        </div>
        <div>
          <ButtonWithPress_s className="automated" onClick={submit} disabled={disabledExportBtn}>Export</ButtonWithPress_s>
        </div>
      </div>
      <div>
        <ExportType_s>
          <label>Export Type</label>
          <ListboxInput
            className="automated"
            field="exportType"
            value={exportType || exportTypes[0]['value']}
            multiple={false}
            persist={(field: string, val: string) => {
              if (val) setExportType(val)
            }}
            options={exportTypes as any}
          />
        </ExportType_s>
        {isFolder && data.length > 0 && <PropertyTable_s>
          <label>Properties to be Exported</label>
          <DataTable_s style={{ overflowY: 'visible' }}>
            <SelectAll_s>
              <Checkbox className="automated" onChange={e => e.checked ? updateSelected(data) : updateSelected([])} checked={selected.length === data.length}></Checkbox>
            </SelectAll_s>
            <DataTable
              value={data}
              paginator={true}
              rows={20}
              rowsPerPageOptions={[10, 20, 50, 100, 500]}
              emptyMessage="No records found"
              selection={selected}
              onSelectionChange={(e) => e.value.length === data.length ? selected.length === data.length ? updateSelected([]) : updateSelected(e.value) : updateSelected(e.value)}
              footer={
                <Label_s style={{ float: 'left', position: 'absolute', zIndex: '2' }}>
                  {selected.length} selected properties
                </Label_s>
              }>
              <Column selectionMode="multiple" />
              <Column
                field="code"
                header="Property"
                filter={true}
                sortable={true}
                style={{ width: '300px' }}
                body={(rowData, props) => `${rowData.code} ${rowData.name ? `- ${rowData.name}` : ''}`}
              />
              <Column
                field="analysisbegin"
                header="Analysis Begin"
                body={(rowData, props) => Moment(rowData.analysisbegin).format('MM/DD/YYYY')}
                filter={true}
                sortable={true}
                style={{ width: '130px' }}
              />
              <Column
                field="reportbegin"
                header="Report Begin"
                body={(rowData, props) => Moment(rowData.reportbegin).format('MM/DD/YYYY')}
                filter={true}
                sortable={true}
                style={{ width: '120px' }}
              />
              <Column field="term" header="Term" filter={true} sortable={true} style={{ width: '80px' }} />
              <Column
                field="calcstatus"
                header="Calculation Status"
                filter={true}
                sortable={true}
                style={{ width: '150px' }}
                body={(rowData, props) => rowData.calcdetail?.status || 'Uncalculated'}
              />
            </DataTable>
          </DataTable_s>
        </PropertyTable_s>}
      </div>
      <FormAutomation automationContainerId="biDataExport" />
    </BiDataExport_s>
  )
}

export default BiDataExport
